"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import { subscriptionService, Subscription } from '@/lib/services/subscriptionService';
import { useAuth } from '@/contexts/AuthContext';

type SettingsTab = 'profile' | 'subscription' | 'api' | 'alerts';

interface ToggleProps {
  enabled: boolean;
  onChange: () => void;
}

function Toggle({ enabled, onChange }: ToggleProps) {
  return (
    <button
      onClick={onChange}
      className="relative inline-flex items-center flex-shrink-0 rounded-full transition-all duration-300 focus:outline-none"
      style={{
        width: '44px',
        height: '24px',
        background: enabled ? 'rgba(163,212,255,0.25)' : 'rgba(255,255,255,0.08)',
        border: enabled ? '1px solid rgba(163,212,255,0.5)' : '1px solid rgba(255,255,255,0.12)',
      }}
    >
      <span
        className="inline-block rounded-full transition-all duration-300"
        style={{
          width: '16px',
          height: '16px',
          background: enabled ? '#A3D4FF' : '#4A6080',
          transform: enabled ? 'translateX(22px)' : 'translateX(3px)',
          boxShadow: enabled ? '0 0 8px rgba(163,212,255,0.6)' : 'none',
        }}
      />
    </button>
  );
}

const sidebarItems: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
  {
    id: 'profile',
    label: 'Profile',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
        <path d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'subscription',
    label: 'Subscription',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="4" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M1 7h14" stroke="currentColor" strokeWidth="1.5" />
        <path d="M4 10.5h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'api',
    label: 'API Keys',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="5.5" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8.5 8h6M12 6v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'alerts',
    label: 'Alert Preferences',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.5a4.5 4.5 0 014.5 4.5v2.5l1.5 2.5H2L3.5 8.5V6A4.5 4.5 0 018 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M6.5 13a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

const tierFeatures: Record<string, { features: string[]; color: string; glow: string }> = {
  Silver: {
    features: ['Weekly research digest', 'Basic dividend screener', 'Email alerts (5/day)', 'Community access'],
    color: '#94a3b8',
    glow: 'rgba(148,163,184,0.15)',
  },
  Gold: {
    features: ['Daily research reports', 'Advanced screener', 'Email + SMS alerts (20/day)', 'Model portfolio access', 'Catalyst calendar'],
    color: '#fbbf24',
    glow: 'rgba(251,191,36,0.15)',
  },
  Platinum: {
    features: ['Real-time research feed', 'Full screener + backtesting', 'Unlimited alerts', 'API access (1,000 calls/mo)', 'Priority support'],
    color: '#e2e8f0',
    glow: 'rgba(226,232,240,0.15)',
  },
  Diamond: {
    features: ['Institutional research terminal', 'Algorithmic model portfolios', 'Unlimited alerts + webhooks', 'API access (50,000 calls/mo)', 'Dedicated analyst access', 'White-glove onboarding'],
    color: '#A3D4FF',
    glow: 'rgba(74,144,226,0.25)',
  },
};

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subLoading, setSubLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === 'subscription' && user) {
      setSubLoading(true);
      setSubError(null);
      subscriptionService.getSubscription()
        .then((sub) => setSubscription(sub))
        .catch((err) => setSubError(err?.message ?? 'Failed to load subscription'))
        .finally(() => setSubLoading(false));
    }
  }, [activeTab, user]);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setSubError(null);
    try {
      const url = await subscriptionService.createPortalSession();
      if (url) window.location.href = url;
    } catch (err: any) {
      setSubError(err?.message ?? 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  // Profile state
  const [profile, setProfile] = useState({
    firstName: 'James',
    lastName: 'Donovan',
    email: 'j.donovan@capitalgroup.com',
    organization: 'Donovan Capital Group',
    role: 'Chief Investment Officer',
    timezone: 'America/New_York',
  });

  // Alert preferences state
  const [alertPrefs, setAlertPrefs] = useState({
    emailAlerts: true,
    inAppAlerts: true,
    smsAlerts: false,
    webhookAlerts: true,
    dividendSignals: true,
    forexSignals: true,
    cryptoSignals: true,
    macroEvents: true,
    earningsAlerts: true,
    riskFlags: true,
    weeklyDigest: true,
    monthlyReport: true,
  });

  // API keys state
  const [apiKeys] = useState([
    { id: 'key_1', name: 'Production Terminal', key: 'dc_live_••••••••••••••••3f9a', created: 'Jan 12, 2025', calls: '2,840', status: 'active' },
    { id: 'key_2', name: 'Analytics Integration', key: 'dc_live_••••••••••••••••7b2c', created: 'Mar 3, 2025', calls: '412', status: 'active' },
    { id: 'key_3', name: 'Staging Environment', key: 'dc_test_••••••••••••••••1d4e', created: 'Apr 1, 2025', calls: '88', status: 'inactive' },
  ]);

  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [copiedKey, setCopiedKey] = useState('');

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleGenerateKey = () => {
    if (!newKeyName.trim()) return;
    setGeneratedKey(`dc_live_${Math.random().toString(36).slice(2, 18)}${Math.random().toString(36).slice(2, 6)}`);
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(''), 2000);
  };

  const toggleAlert = (key: keyof typeof alertPrefs) => {
    setAlertPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div style={{ background: '#0A2540', minHeight: '100vh' }}>
      <Header />

      <div className="flex" style={{ paddingTop: '72px', minHeight: '100vh' }}>

        {/* ── SIDEBAR ── */}
        <aside
          className={`fixed md:sticky top-0 md:top-[72px] left-0 h-full md:h-[calc(100vh-72px)] flex-shrink-0 z-30 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
          style={{
            width: '240px',
            background: '#1A2A44',
            borderRight: '1px solid #1E3A5F',
            overflowY: 'auto',
            paddingTop: '24px',
          }}
        >
          {/* Member info */}
          <div className="px-5 mb-6">
            <div className="text-xs font-mono uppercase tracking-widest mb-1"
              style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em', opacity: 0.6 }}>
              Account Settings
            </div>
            <div className="flex items-center gap-2 mt-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: 'linear-gradient(135deg, #4A90E2, #A3D4FF)', color: '#0A2540' }}>
                JD
              </div>
              <div>
                <div className="text-sm font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>J. Donovan</div>
                <div className="text-xs font-mono" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', fontSize: '10px' }}>Diamond Member</div>
              </div>
            </div>
          </div>

          <div className="h-px mx-5 mb-5" style={{ background: '#1E3A5F' }} />

          {/* Nav items */}
          <nav className="px-3">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-1 text-sm font-medium transition-all duration-200"
                style={{
                  background: activeTab === item.id ? 'rgba(163,212,255,0.1)' : 'transparent',
                  color: activeTab === item.id ? '#A3D4FF' : '#E3F2FD',
                  opacity: activeTab === item.id ? 1 : 0.55,
                  border: activeTab === item.id ? '1px solid rgba(163,212,255,0.2)' : '1px solid transparent',
                  fontFamily: 'var(--font-body)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </nav>

          <div className="h-px mx-5 my-5" style={{ background: '#1E3A5F' }} />

          {/* Back to dashboard */}
          <div className="px-5">
            <Link
              href="/dashboard"
              className="flex items-center gap-2 text-xs font-semibold transition-colors duration-200"
              style={{ color: '#4A90E2', fontFamily: 'var(--font-body)' }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Dashboard
            </Link>
          </div>
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-20 md:hidden" style={{ background: 'rgba(10,37,64,0.7)' }}
            onClick={() => setSidebarOpen(false)} />
        )}

        {/* ── MAIN CONTENT ── */}
        <main className="flex-1 overflow-auto" style={{ minWidth: 0 }}>
          {/* Mobile menu toggle */}
          <div className="md:hidden flex items-center gap-3 px-6 py-4" style={{ borderBottom: '1px solid #1E3A5F' }}>
            <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2 5h14M2 9h14M2 13h14" stroke="#A3D4FF" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>
              {sidebarItems.find(s => s.id === activeTab)?.label}
            </span>
          </div>

          <div className="p-6 md:p-8 max-w-4xl">

            {/* ── PROFILE TAB ── */}
            {activeTab === 'profile' && (
              <div>
                <div className="mb-8">
                  <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>Profile Information</h1>
                  <p className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>Manage your personal details and institutional affiliation</p>
                </div>

                {/* Avatar row */}
                <div className="flex items-center gap-5 p-6 rounded-xl mb-6" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #4A90E2, #A3D4FF)', color: '#0A2540' }}>
                    JD
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-white mb-0.5" style={{ fontFamily: 'var(--font-display)' }}>Profile Photo</div>
                    <div className="text-xs mb-3" style={{ color: '#E3F2FD', opacity: 0.45 }}>JPG or PNG, max 2MB. Displayed in your member portal.</div>
                    <button className="text-xs font-semibold px-4 py-2 rounded-lg"
                      style={{ background: 'rgba(163,212,255,0.08)', border: '1px solid rgba(163,212,255,0.2)', color: '#A3D4FF', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                      Upload Photo
                    </button>
                  </div>
                </div>

                {/* Form fields */}
                <div className="p-6 rounded-xl mb-6" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                  <div className="text-xs font-mono uppercase tracking-widest mb-5" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', opacity: 0.7 }}>
                    Personal Details
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[
                      { label: 'First Name', key: 'firstName' as const, placeholder: 'James' },
                      { label: 'Last Name', key: 'lastName' as const, placeholder: 'Donovan' },
                      { label: 'Email Address', key: 'email' as const, placeholder: 'email@institution.com' },
                      { label: 'Organization', key: 'organization' as const, placeholder: 'Firm or Family Office' },
                      { label: 'Role / Title', key: 'role' as const, placeholder: 'Chief Investment Officer' },
                      { label: 'Timezone', key: 'timezone' as const, placeholder: 'America/New_York' },
                    ].map((field) => (
                      <div key={field.key}>
                        <label className="block text-xs font-mono mb-2" style={{ color: '#E3F2FD', opacity: 0.5, fontFamily: 'var(--font-mono)' }}>
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={profile[field.key]}
                          onChange={(e) => setProfile((prev) => ({ ...prev, [field.key]: e.target.value }))}
                          placeholder={field.placeholder}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                          style={{
                            background: '#0A2540',
                            border: '1px solid #1E3A5F',
                            color: '#FFFFFF',
                            fontFamily: 'var(--font-body)',
                          }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(163,212,255,0.4)'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#1E3A5F'; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Password section */}
                <div className="p-6 rounded-xl mb-6" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                  <div className="text-xs font-mono uppercase tracking-widest mb-5" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', opacity: 0.7 }}>
                    Security
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {['Current Password', 'New Password', 'Confirm New Password'].map((label) => (
                      <div key={label} className={label === 'Current Password' ? 'md:col-span-2' : ''}>
                        <label className="block text-xs font-mono mb-2" style={{ color: '#E3F2FD', opacity: 0.5, fontFamily: 'var(--font-mono)' }}>
                          {label}
                        </label>
                        <input
                          type="password"
                          placeholder="••••••••••••"
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                          style={{
                            background: '#0A2540',
                            border: '1px solid #1E3A5F',
                            color: '#FFFFFF',
                            fontFamily: 'var(--font-body)',
                          }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(163,212,255,0.4)'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#1E3A5F'; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Save button */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleSave}
                    className="px-6 py-3 rounded-xl text-sm font-bold transition-all duration-200"
                    style={{
                      background: '#A3D4FF',
                      color: '#0A2540',
                      fontFamily: 'var(--font-body)',
                      border: '2px solid #A3D4FF',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#A3D4FF'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#A3D4FF'; e.currentTarget.style.color = '#0A2540'; }}
                  >
                    Save Changes
                  </button>
                  {saved && (
                    <span className="text-xs font-mono flex items-center gap-2" style={{ color: '#4ade80', fontFamily: 'var(--font-mono)' }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2 7l3.5 3.5L12 3" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Changes saved
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ── SUBSCRIPTION TAB ── */}
            {activeTab === 'subscription' && (
              <div>
                <div className="mb-8">
                  <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>Subscription Tier</h1>
                  <p className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>Manage your membership plan and billing details</p>
                </div>

                {subError && (
                  <div className="mb-6 p-4 rounded-xl text-sm" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', fontFamily: 'var(--font-body)' }}>
                    {subError}
                  </div>
                )}

                {/* Current plan banner */}
                {subLoading ? (
                  <div className="p-6 rounded-xl mb-8 flex items-center gap-3" style={{ background: 'rgba(163,212,255,0.04)', border: '1px solid rgba(163,212,255,0.15)' }}>
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="6" stroke="#A3D4FF" strokeWidth="1.5" strokeDasharray="24" strokeDashoffset="12" />
                    </svg>
                    <span className="text-sm" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)' }}>Loading subscription...</span>
                  </div>
                ) : subscription && subscriptionService.isActive(subscription) ? (
                  <div className="p-6 rounded-xl mb-8" style={{ background: 'rgba(74,144,226,0.08)', border: '1px solid rgba(74,144,226,0.3)', boxShadow: '0 0 24px rgba(74,144,226,0.08)' }}>
                    <div className="flex items-start justify-between flex-wrap gap-4">
                      <div>
                        <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: '#4A90E2', fontFamily: 'var(--font-mono)' }}>
                          Current Plan
                        </div>
                        <div className="text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-display)', color: subscriptionService.getTierColor(subscription.tier) }}>
                          {subscriptionService.getTierLabel(subscription.tier)} Tier
                        </div>
                        {subscription.currentPeriodEnd && (
                          <div className="text-sm" style={{ color: '#E3F2FD', opacity: 0.55 }}>
                            {subscription.cancelAtPeriodEnd ? 'Cancels' : 'Renews'} {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-mono"
                        style={{
                          background: subscription.status === 'active' ? 'rgba(74,222,128,0.08)' : 'rgba(251,191,36,0.08)',
                          border: `1px solid ${subscription.status === 'active' ? 'rgba(74,222,128,0.2)' : 'rgba(251,191,36,0.2)'}`,
                          color: subscription.status === 'active' ? '#4ade80' : '#fbbf24',
                          fontFamily: 'var(--font-mono)',
                        }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: subscription.status === 'active' ? '#4ade80' : '#fbbf24', boxShadow: `0 0 6px ${subscription.status === 'active' ? '#4ade80' : '#fbbf24'}` }} />
                        {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 rounded-xl mb-8" style={{ background: 'rgba(163,212,255,0.04)', border: '1px solid rgba(163,212,255,0.15)' }}>
                    <div className="text-sm mb-3" style={{ color: '#E3F2FD', opacity: 0.7 }}>You do not have an active subscription.</div>
                    <Link
                      href="/homepage#pricing"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
                      style={{ background: '#A3D4FF', color: '#0A2540', fontFamily: 'var(--font-body)' }}
                    >
                      View Plans
                    </Link>
                  </div>
                )}

                {/* Tier comparison */}
                <div className="text-xs font-mono uppercase tracking-widest mb-5" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', opacity: 0.7 }}>
                  Available Plans
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {Object.entries(tierFeatures).map(([tier, data]) => {
                    const isCurrent = subscription?.tier === tier.toLowerCase() && subscriptionService.isActive(subscription);
                    return (
                      <div
                        key={tier}
                        className="p-6 rounded-xl transition-all duration-200"
                        style={{
                          background: isCurrent ? 'rgba(74,144,226,0.06)' : '#1A2A44',
                          border: isCurrent ? `1px solid rgba(74,144,226,0.4)` : '1px solid #1E3A5F',
                          boxShadow: isCurrent ? `0 0 20px ${data.glow}` : 'none',
                        }}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-base font-bold" style={{ color: data.color, fontFamily: 'var(--font-display)' }}>{tier}</div>
                          {isCurrent && (
                            <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(74,144,226,0.15)', color: '#4A90E2', fontFamily: 'var(--font-mono)' }}>
                              Current
                            </span>
                          )}
                        </div>
                        <ul className="space-y-2 mb-5">
                          {data.features.map((f) => (
                            <li key={f} className="flex items-start gap-2 text-xs" style={{ color: '#E3F2FD', opacity: 0.65 }}>
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="flex-shrink-0 mt-0.5">
                                <path d="M2 6l2.5 2.5L10 3" stroke={data.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              {f}
                            </li>
                          ))}
                        </ul>
                        {!isCurrent && (
                          <Link
                            href="/homepage#pricing"
                            className="w-full py-2.5 rounded-xl text-xs font-bold transition-all duration-200 block text-center"
                            style={{
                              background: 'rgba(163,212,255,0.06)',
                              border: '1px solid rgba(163,212,255,0.2)',
                              color: '#A3D4FF',
                              fontFamily: 'var(--font-body)',
                            }}
                          >
                            Subscribe to {tier}
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Billing management */}
                <div className="p-6 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                  <div className="text-xs font-mono uppercase tracking-widest mb-5" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', opacity: 0.7 }}>
                    Billing Management
                  </div>
                  <p className="text-sm mb-5" style={{ color: '#E3F2FD', opacity: 0.55, fontFamily: 'var(--font-body)' }}>
                    Manage your payment method, billing cycle, invoices, and subscription cancellation through the Stripe billing portal.
                  </p>
                  <button
                    onClick={handleManageBilling}
                    disabled={portalLoading || !subscription}
                    className="px-6 py-3 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2"
                    style={{
                      background: subscription ? '#A3D4FF' : 'rgba(163,212,255,0.1)',
                      color: subscription ? '#0A2540' : '#A3D4FF',
                      fontFamily: 'var(--font-body)',
                      border: `2px solid ${subscription ? '#A3D4FF' : 'rgba(163,212,255,0.2)'}`,
                      cursor: subscription && !portalLoading ? 'pointer' : 'not-allowed',
                      opacity: portalLoading ? 0.7 : 1,
                    }}
                  >
                    {portalLoading ? (
                      <>
                        <svg className="animate-spin" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="20" strokeDashoffset="10" />
                        </svg>
                        Opening Portal...
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M1 6h12" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                        Manage Billing
                      </>
                    )}
                  </button>
                  {!subscription && !subLoading && (
                    <p className="text-xs mt-3" style={{ color: '#E3F2FD', opacity: 0.35, fontFamily: 'var(--font-mono)' }}>
                      Subscribe to a plan to access billing management.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── API KEYS TAB ── */}
            {activeTab === 'api' && (
              <div>
                <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>API Keys</h1>
                    <p className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>Manage programmatic access to the DividendChase data terminal</p>
                  </div>
                  <button
                    onClick={() => { setShowNewKeyModal(true); setGeneratedKey(''); setNewKeyName(''); }}
                    className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200"
                    style={{
                      background: '#A3D4FF',
                      color: '#0A2540',
                      fontFamily: 'var(--font-body)',
                      border: '2px solid #A3D4FF',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#A3D4FF'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#A3D4FF'; e.currentTarget.style.color = '#0A2540'; }}
                  >
                    + Generate New Key
                  </button>
                </div>

                {/* Usage summary */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[
                    { label: 'Total API Calls', value: '3,340', sub: 'This month' },
                    { label: 'Monthly Limit', value: '50,000', sub: 'Diamond tier' },
                    { label: 'Active Keys', value: '2', sub: 'of 3 total' },
                  ].map((stat) => (
                    <div key={stat.label} className="p-5 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                      <div className="text-xs font-mono mb-2" style={{ color: '#E3F2FD', opacity: 0.4, fontFamily: 'var(--font-mono)' }}>{stat.label}</div>
                      <div className="text-2xl font-bold text-white mb-0.5" style={{ fontFamily: 'var(--font-display)', fontFeatureSettings: '"tnum"' }}>{stat.value}</div>
                      <div className="text-xs" style={{ color: '#E3F2FD', opacity: 0.35 }}>{stat.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Keys list */}
                <div className="space-y-4 mb-8">
                  {apiKeys.map((key) => (
                    <div key={key.id} className="p-6 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <span className="text-sm font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>{key.name}</span>
                            <span
                              className="text-xs font-mono px-2 py-0.5 rounded"
                              style={{
                                background: key.status === 'active' ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
                                color: key.status === 'active' ? '#4ade80' : '#f87171',
                                fontFamily: 'var(--font-mono)',
                              }}
                            >
                              {key.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.35, fontFamily: 'var(--font-mono)' }}>
                            Created {key.created} · {key.calls} calls this month
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                            style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: '#0A2540', border: '1px solid #1E3A5F' }}>
                        <code className="flex-1 text-xs font-mono truncate" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)' }}>
                          {key.key}
                        </code>
                        <button
                          onClick={() => handleCopy(key.key, key.id)}
                          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0 transition-all duration-200"
                          style={{
                            background: copiedKey === key.id ? 'rgba(74,222,128,0.1)' : 'rgba(163,212,255,0.08)',
                            border: copiedKey === key.id ? '1px solid rgba(74,222,128,0.3)' : '1px solid rgba(163,212,255,0.2)',
                            color: copiedKey === key.id ? '#4ade80' : '#A3D4FF',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-body)',
                          }}
                        >
                          {copiedKey === key.id ? (
                            <>
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6l2.5 2.5L10 3" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              Copied
                            </>
                          ) : (
                            <>
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <rect x="4" y="4" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2" />
                                <path d="M8 4V2a1 1 0 00-1-1H2a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="currentColor" strokeWidth="1.2" />
                              </svg>
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Docs link */}
                <div className="p-5 rounded-xl flex items-center gap-4" style={{ background: 'rgba(74,144,226,0.06)', border: '1px solid rgba(74,144,226,0.2)' }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="flex-shrink-0">
                    <rect x="3" y="2" width="14" height="16" rx="2" stroke="#4A90E2" strokeWidth="1.5" />
                    <path d="M7 7h6M7 10h6M7 13h4" stroke="#4A90E2" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-white mb-0.5" style={{ fontFamily: 'var(--font-display)' }}>API Documentation</div>
                    <div className="text-xs" style={{ color: '#E3F2FD', opacity: 0.45 }}>Full reference for endpoints, rate limits, and data schemas</div>
                  </div>
                  <button className="text-xs font-semibold flex-shrink-0" style={{ color: '#4A90E2', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                    View Docs →
                  </button>
                </div>

                {/* New Key Modal */}
                {showNewKeyModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(10,37,64,0.85)', backdropFilter: 'blur(8px)' }}>
                    <div className="w-full max-w-md p-8 rounded-2xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                      <div className="flex items-center justify-between mb-6">
                        <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>Generate API Key</div>
                        <button onClick={() => setShowNewKeyModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E3F2FD', opacity: 0.4 }}>
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>

                      {!generatedKey ? (
                        <>
                          <label className="block text-xs font-mono mb-2" style={{ color: '#E3F2FD', opacity: 0.5, fontFamily: 'var(--font-mono)' }}>
                            Key Name / Label
                          </label>
                          <input
                            type="text"
                            value={newKeyName}
                            onChange={(e) => setNewKeyName(e.target.value)}
                            placeholder="e.g. Production Integration"
                            className="w-full px-4 py-3 rounded-xl text-sm outline-none mb-6"
                            style={{
                              background: '#0A2540',
                              border: '1px solid #1E3A5F',
                              color: '#FFFFFF',
                              fontFamily: 'var(--font-body)',
                            }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(163,212,255,0.4)'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = '#1E3A5F'; }}
                          />
                          <button
                            onClick={handleGenerateKey}
                            disabled={!newKeyName.trim()}
                            className="w-full py-3 rounded-xl text-sm font-bold transition-all duration-200"
                            style={{
                              background: newKeyName.trim() ? '#A3D4FF' : 'rgba(163,212,255,0.2)',
                              color: newKeyName.trim() ? '#0A2540' : 'rgba(163,212,255,0.4)',
                              fontFamily: 'var(--font-body)',
                              cursor: newKeyName.trim() ? 'pointer' : 'not-allowed',
                              border: 'none',
                            }}
                          >
                            Generate Key
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.2)' }}>
                            <div className="text-xs font-mono mb-2" style={{ color: '#4ade80', fontFamily: 'var(--font-mono)' }}>
                              ✓ Key generated — copy it now. It will not be shown again.
                            </div>
                            <code className="block text-xs font-mono break-all" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)' }}>
                              {generatedKey}
                            </code>
                          </div>
                          <button
                            onClick={() => handleCopy(generatedKey, 'modal')}
                            className="w-full py-3 rounded-xl text-sm font-bold mb-3 transition-all duration-200"
                            style={{
                              background: copiedKey === 'modal' ? 'rgba(74,222,128,0.15)' : '#A3D4FF',
                              color: copiedKey === 'modal' ? '#4ade80' : '#0A2540',
                              fontFamily: 'var(--font-body)',
                              cursor: 'pointer',
                              border: copiedKey === 'modal' ? '1px solid rgba(74,222,128,0.3)' : 'none',
                            }}
                          >
                            {copiedKey === 'modal' ? '✓ Copied to Clipboard' : 'Copy API Key'}
                          </button>
                          <button
                            onClick={() => setShowNewKeyModal(false)}
                            className="w-full py-2.5 rounded-xl text-sm font-semibold"
                            style={{ background: 'transparent', border: '1px solid #1E3A5F', color: '#E3F2FD', opacity: 0.5, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
                          >
                            Done
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── ALERT PREFERENCES TAB ── */}
            {activeTab === 'alerts' && (
              <div>
                <div className="mb-8">
                  <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>Alert Preferences</h1>
                  <p className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>Configure delivery channels and signal categories for your intelligence feed</p>
                </div>

                {/* Delivery channels */}
                <div className="p-6 rounded-xl mb-6" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                  <div className="text-xs font-mono uppercase tracking-widest mb-5" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', opacity: 0.7 }}>
                    Delivery Channels
                  </div>
                  <div className="space-y-5">
                    {[
                      { key: 'emailAlerts' as const, label: 'Email Alerts', desc: 'Signals and reports delivered to j.donovan@capitalgroup.com' },
                      { key: 'inAppAlerts' as const, label: 'In-App Notifications', desc: 'Real-time alerts within the member terminal' },
                      { key: 'smsAlerts' as const, label: 'SMS Alerts', desc: 'Critical signals via text message (Diamond tier)' },
                      { key: 'webhookAlerts' as const, label: 'Webhook Delivery', desc: 'Push alerts to your custom endpoint via HTTP POST' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between">
                        <div className="flex-1 pr-6">
                          <div className="text-sm font-semibold text-white mb-0.5" style={{ fontFamily: 'var(--font-body)' }}>{item.label}</div>
                          <div className="text-xs" style={{ color: '#E3F2FD', opacity: 0.45 }}>{item.desc}</div>
                        </div>
                        <Toggle enabled={alertPrefs[item.key]} onChange={() => toggleAlert(item.key)} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Signal categories */}
                <div className="p-6 rounded-xl mb-6" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                  <div className="text-xs font-mono uppercase tracking-widest mb-5" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', opacity: 0.7 }}>
                    Signal Categories
                  </div>
                  <div className="space-y-5">
                    {[
                      { key: 'dividendSignals' as const, label: 'Dividend Signals', desc: 'Buy/sell signals, sustainability scores, payout ratio alerts' },
                      { key: 'forexSignals' as const, label: 'Forex Signals', desc: 'Algorithmic setups, macro divergence, support/resistance triggers' },
                      { key: 'cryptoSignals' as const, label: 'Digital Asset Signals', desc: 'On-chain metrics, whale activity, momentum alerts' },
                      { key: 'macroEvents' as const, label: 'Macro Events', desc: 'Fed decisions, ECB statements, CPI/NFP releases' },
                      { key: 'earningsAlerts' as const, label: 'Earnings Alerts', desc: 'Pre/post earnings analysis for covered dividend equities' },
                      { key: 'riskFlags' as const, label: 'Risk Flags', desc: 'Payout stress tests, credit downgrades, liquidity warnings' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between">
                        <div className="flex-1 pr-6">
                          <div className="text-sm font-semibold text-white mb-0.5" style={{ fontFamily: 'var(--font-body)' }}>{item.label}</div>
                          <div className="text-xs" style={{ color: '#E3F2FD', opacity: 0.45 }}>{item.desc}</div>
                        </div>
                        <Toggle enabled={alertPrefs[item.key]} onChange={() => toggleAlert(item.key)} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reports & digests */}
                <div className="p-6 rounded-xl mb-8" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                  <div className="text-xs font-mono uppercase tracking-widest mb-5" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', opacity: 0.7 }}>
                    Reports & Digests
                  </div>
                  <div className="space-y-5">
                    {[
                      { key: 'weeklyDigest' as const, label: 'Weekly Intelligence Digest', desc: 'Curated summary of top signals and research every Monday' },
                      { key: 'monthlyReport' as const, label: 'Monthly Portfolio Report', desc: 'Full performance attribution and model portfolio review' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between">
                        <div className="flex-1 pr-6">
                          <div className="text-sm font-semibold text-white mb-0.5" style={{ fontFamily: 'var(--font-body)' }}>{item.label}</div>
                          <div className="text-xs" style={{ color: '#E3F2FD', opacity: 0.45 }}>{item.desc}</div>
                        </div>
                        <Toggle enabled={alertPrefs[item.key]} onChange={() => toggleAlert(item.key)} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Save button */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleSave}
                    className="px-6 py-3 rounded-xl text-sm font-bold transition-all duration-200"
                    style={{
                      background: '#A3D4FF',
                      color: '#0A2540',
                      fontFamily: 'var(--font-body)',
                      border: '2px solid #A3D4FF',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#A3D4FF'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#A3D4FF'; e.currentTarget.style.color = '#0A2540'; }}
                  >
                    Save Preferences
                  </button>
                  {saved && (
                    <span className="text-xs font-mono flex items-center gap-2" style={{ color: '#4ade80', fontFamily: 'var(--font-mono)' }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2 7l3.5 3.5L12 3" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Preferences saved
                    </span>
                  )}
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}
